<template>
    <c-app-panel
        v-if           = "items.length > 0"
        :icon          = "counts.active > 0 ? 'mdi-upload' : counts.failed > 0 ? 'mdi-alert-circle' : 'mdi-check-circle'"
        :icon-color    = "counts.failed > 0 && counts.active === 0 ? 'error' : 'primary'"
        :title         = "title"
        :progress      = "overall"
        :progress-color = "counts.failed > 0 ? 'warning' : 'primary'"
        collapsible
        :close-disabled = "counts.active > 0"
        @close         = "onClose"
    >
        <div class="upload-content">
            <div v-for="item in uploading" :key="item.key" class="upload-item">
                <div class="upload-name">{{ item.file.name }}</div>
                <div class="upload-where">{{ item.siteLabel }} / {{ item.folderLabel }}</div>
                <v-progress-linear :value="item.progress * 100" height="6" color="primary" rounded />
            </div>

            <div v-if="counts.active > 0" class="upload-summary">
                <span v-if="waiting > 0">{{ $t('img.upload.waiting', { n: waiting }) }}</span>
                <span v-if="processing > 0">{{ $t('img.upload.processing', { n: processing }) }}</span>
            </div>

            <div v-for="item in skipped" :key="item.key" class="upload-item">
                <div class="upload-name">{{ item.file.name }}</div>
                <div class="upload-where">
                    {{ item.siteLabel }} / {{ item.folderLabel }} · {{ $t('img.upload.skipped') }}
                </div>
            </div>

            <div v-for="item in failed" :key="item.key" class="upload-item">
                <div class="upload-name error--text">{{ item.file.name }}</div>
                <div class="upload-where">
                    {{ item.siteLabel }} / {{ item.folderLabel }} · {{ reason(item) }}
                    <a v-if="retryable(item)" class="ml-1" @click="Uploads.retry(item)">{{ $t('img.upload.retry') }}</a>
                </div>
            </div>
        </div>
    </c-app-panel>
</template>

<script>
import Core from "@/modules/core/service/Core";
import CAppPanel from "@/modules/core/components/app/CAppPanel.vue";
import Uploads from "@/modules/img/components/service/Uploads";
import { FAILED, IN_PROGRESS, NOT_RETRYABLE, SKIPPED, UPLOADING, WAITING } from "@/modules/img/components/service/UploadQueue";

export default {
    name: "ImgUploadPanel",

    components: { CAppPanel },

    data: () => ({
        Uploads,
        stopFinished: null,
    }),

    computed: {
        items () {
            return Uploads.state.items;
        },

        counts () {
            return Uploads.counts();
        },

        uploading () {
            return this.items.filter(item => item.state === UPLOADING);
        },

        failed () {
            return this.items.filter(item => item.state === FAILED);
        },

        skipped () {
            return this.items.filter(item => item.state === SKIPPED);
        },

        waiting () {
            return this.items.filter(item => item.state === WAITING).length;
        },

        processing () {
            return this.items.filter(item => item.state === IN_PROGRESS).length;
        },

        overall () {
            const { total, done, failed, skipped } = this.counts;
            return total === 0 ? 0 : ((done + failed + skipped) * 100) / total;
        },

        title () {
            const { total, done, failed, skipped, active } = this.counts;
            if (active > 0) {
                return this.$t("img.upload.running", { done: done + failed + skipped, total });
            }
            return this.summary({ done, failed, skipped });
        }
    },

    mounted () {
        this.stopFinished = Uploads.onFinished(result => Core.message.hint(this, this.summary(result)));
        window.addEventListener("beforeunload", this.onBeforeUnload);
    },

    beforeDestroy () {
        if (this.stopFinished) {
            this.stopFinished();
        }
        window.removeEventListener("beforeunload", this.onBeforeUnload);
    },

    methods: {
        // Closing the tab would cut off what is still waiting or running
        onBeforeUnload (event) {
            if (Uploads.counts().active > 0) {
                event.preventDefault();
                event.returnValue = "";
            }
        },

        onClose () {
            Uploads.clear();
        },

        /** "3 hochgeladen, 1 übersprungen, 2 fehlgeschlagen": only what happened. */
        summary ({ done, failed, skipped }) {
            if (failed === 0 && skipped === 0) {
                return this.$t("img.upload.finished", { done });
            }
            const parts = [this.$t("img.upload.summary.done", { n: done })];
            if (skipped > 0) {
                parts.push(this.$t("img.upload.summary.skipped", { n: skipped }));
            }
            if (failed > 0) {
                parts.push(this.$t("img.upload.summary.failed", { n: failed }));
            }
            return parts.join(", ");
        },

        retryable (item) {
            return !NOT_RETRYABLE.includes(item.reason);
        },

        reason (item) {
            if (!item.reason) {
                return this.$t("img.upload.state.FAILED");
            }
            const key = "img.upload.reason." + item.reason;
            return this.$te(key) ? this.$t(key, { status: item.status }) : item.reason;
        }
    }
}
</script>

<style scoped>
.upload-item {
    margin-top: 8px;
    font-size: 13px;
}

.upload-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.upload-where, .upload-summary {
    font-size: 12px;
    color: gray;
    margin-bottom: 2px;
}

.upload-summary span + span {
    margin-left: 10px;
}
</style>
